import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { constants } from '../../../../config';
import { getDashboardMapCoordinates } from '../../../../helpers/water_rights/backend_helper';

mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

// Default center: Chile
const DEFAULT_CENTER = [-72.5, -40.5];
const DEFAULT_ZOOM = 5;

const isValidCoord = (lat, lng) =>
	lat != null &&
	lng != null &&
	Math.abs(lat) <= 90 &&
	Math.abs(lng) <= 180;

const DashboardMap = () => {
	const mapContainer = useRef(null);
	const mapRef = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const [features, setFeatures] = useState([]);

	// Fetch coordinates on mount
	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const res = await getDashboardMapCoordinates();
				const raw = Array.isArray(res) ? res : [];

				const mapped = raw
					.filter(item => isValidCoord(item.latitude, item.longitude))
					.map(item => ({
						type: 'Feature',
						geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
						properties: {
							name: item.name || 'Sin nombre',
							coordType: item.coord_type || '',
							color: item.color || '#15aabf',
							status: item.daa_status || 'Sin estado',
							fileNumber: item.file_number || '',
							establishment: item.establishment_name || '',
						},
					}));

				if (!cancelled) setFeatures(mapped);
			} catch {
				// Map still renders without points
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		load();
		return () => { cancelled = true; };
	}, []);

	// Init / update map whenever data is ready
	useEffect(() => {
		if (isLoading || !mapContainer.current) return;

		if (mapRef.current) {
			mapRef.current.remove();
			mapRef.current = null;
		}

		const map = new mapboxgl.Map({
			container: mapContainer.current,
			style: 'mapbox://styles/mapbox/satellite-streets-v11',
			center: DEFAULT_CENTER,
			zoom: DEFAULT_ZOOM,
		});

		mapRef.current = map;
		map.addControl(new mapboxgl.NavigationControl(), 'top-right');

		map.on('load', () => {
			map.addSource('coordinates', {
				type: 'geojson',
				data: { type: 'FeatureCollection', features },
			});
			map.addLayer({
				id: 'coord-circles',
				type: 'circle',
				source: 'coordinates',
				paint: {
					'circle-radius': 9,
					'circle-color': ['get', 'color'],
					'circle-opacity': 0.92,
				},
			});

			// Auto-fit bounds
			if (features.length > 0) {
				const bounds = features.reduce(
					(b, f) => b.extend(f.geometry.coordinates),
					new mapboxgl.LngLatBounds(
						features[0].geometry.coordinates,
						features[0].geometry.coordinates
					)
				);
				map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 0 });
			}

			// Popup + cursor
			map.on('click', 'coord-circles', e => {
				const coords = e.features[0].geometry.coordinates.slice();
				const { name, coordType, status, fileNumber, establishment, color } = e.features[0].properties;
				const typeLabel = coordType === 'captacion' ? 'Captación' : 'Restitución';
				const fileRow = fileNumber ? `<div style="color:#555;font-size:11px;">Expediente: ${fileNumber}</div>` : '';
				const estRow = establishment ? `<div style="color:#555;font-size:11px;">Centro: ${establishment}</div>` : '';
				new mapboxgl.Popup({ offset: 12 })
					.setLngLat(coords)
					.setHTML(
						`<div style="padding:6px 8px;min-width:160px;">
							
							<div style="font-size:10px;font-weight:600;text-transform:uppercase;color:#555;margin-bottom:4px;">${typeLabel}</div>
							<div style="font-size:13px;font-weight:600;margin-bottom:3px;">${name}</div>
							${estRow}
							${fileRow}
							<div style="font-size:11px;color:#555;">Estado DAA: <span style="font-weight:600;color:${color};">${status}</span></div>
							
						</div>`
					)
					.addTo(map);
			});
			map.on('mouseenter', 'coord-circles', () => { map.getCanvas().style.cursor = 'pointer'; });
			map.on('mouseleave', 'coord-circles', () => { map.getCanvas().style.cursor = ''; });
		});

		return () => {
			if (mapRef.current) {
				mapRef.current.remove();
				mapRef.current = null;
			}
		};
	}, [isLoading, features]);

	return (
		<div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '600px', borderRadius: '4px', overflow: 'hidden' }}>
			<div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

			{isLoading && (
				<div style={{
					position: 'absolute', inset: 0,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					background: 'rgba(0,0,0,0.35)', zIndex: 10,
				}}>
					<div className='spinner-border text-light' role='status'>
						<span className='visually-hidden'>Cargando...</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default DashboardMap;
